import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  MapPin, User, BadgeCheck, Star, Monitor, Users, Globe, Lock, ArrowLeft, Clock, Languages, Mail, CalendarClock,
} from 'lucide-react';
import { useJsonLd, SITE_URL } from './useJsonLd';
import ShareListing from '../../components/ShareListing';
import { supervisionRate } from './SupervisorDirectory';

const SupervisorProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [cName, setCName] = useState('');
  const [cEmail, setCEmail] = useState('');
  const [cState, setCState] = useState('');
  const [cType, setCType] = useState('');
  const [cFormat, setCFormat] = useState('');
  const [sending, setSending] = useState(false);

  useJsonLd('supervisor-profile-schema', (s && !s.is_demo) ? {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: s.full_name,
    jobTitle: `${s.license_type} Clinical Supervisor`,
    url: `${SITE_URL}/supervisors/${s.user_id}`,
    description: s.bio,
    knowsAbout: s.specialties,
    address: {
      '@type': 'PostalAddress',
      addressLocality: s.city, addressRegion: s.license_state, addressCountry: 'US',
    },
    hasCredential: {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'license',
      name: `${s.license_type} (verified via Texas BHEC)`,
    },
  } : null);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/supervisors/${userId}`);
      setS(res.data);
      document.title = `${res.data.full_name} — Clinical Supervisor | I Need A Social Worker`;
    } catch (e) {
      setS(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleContact = (kind) => {
    if (s.is_demo) {
      toast.info('This is a demonstration profile — not a real supervisor. It cannot be contacted.');
      return;
    }
    if (!isAuthenticated) {
      toast.info('Please sign in or create a free account to contact a supervisor.');
      navigate('/login');
      return;
    }
    setMessage(kind === 'consult'
      ? 'I would like to request a consultation to discuss clinical supervision toward my LCSW.'
      : '');
    setOpen(true);
  };

  const submitInquiry = async () => {
    if (!message.trim()) { toast.error('Please write a short message.'); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/supervisors/${userId}/inquire`, {
        message, phone, name: cName || undefined, email: cEmail || undefined,
        state: cState || undefined, supervision_type: cType || undefined, format: cFormat || undefined,
      });
      toast.success('Your inquiry has been sent!');
      setOpen(false);
      setMessage('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send inquiry.');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20">Loading…</div>;
  if (!s) return (
    <div className="min-h-screen flex flex-col items-center justify-center pt-20 gap-3">
      <p className="text-gray-600">Supervisor not found.</p>
      <Link to="/find-a-supervisor"><Button variant="outline">Back to directory</Button></Link>
    </div>
  );

  const deliveryLabel = s.delivery === 'both' ? 'Online & In Person' : s.delivery === 'online' ? 'Online' : 'In Person';
  const supTypeLabel = s.supervision_type === 'both' ? 'Individual & Group' : s.supervision_type === 'individual' ? 'Individual' : 'Group';

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/find-a-supervisor" className="inline-flex items-center gap-1 text-primary hover:text-secondary text-sm mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to supervisors
        </Link>

        <div className="bg-white rounded-2xl border overflow-hidden">
          <div className="bg-primary p-6 sm:p-8 text-white flex flex-col sm:flex-row items-start gap-5">
            {s.photo_url ? (
              <img src={s.photo_url} alt={s.full_name} className="w-24 h-24 rounded-full object-cover ring-4 ring-white/20" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center ring-4 ring-white/20">
                <User className="w-11 h-11 text-white" />
              </div>
            )}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {s.is_featured && !s.is_demo && (
                  <Badge className="bg-[#CBA14B] text-white border-0 flex items-center gap-1 w-fit" data-testid="supervisor-featured-badge"><Star className="w-3.5 h-3.5 fill-white" /> FEATURED</Badge>
                )}
                {!s.is_demo && (
                  <Badge className="bg-green-500 text-white flex items-center gap-1 w-fit"><BadgeCheck className="w-3.5 h-3.5" /> License Verified · Texas BHEC</Badge>
                )}
              </div>
              <h1 className="font-serif text-2xl sm:text-3xl font-bold" data-testid="supervisor-name">{s.full_name}</h1>
              <p className="text-white/80 flex flex-wrap items-center gap-3 mt-2 text-sm">
                <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {s.city ? `${s.city}, ` : ''}{s.license_state}</span>
                <span className="inline-flex items-center gap-1"><Monitor className="w-4 h-4" /> {deliveryLabel}</span>
                <span className="inline-flex items-center gap-1"><Users className="w-4 h-4" /> {supTypeLabel}</span>
              </p>
              <p className="text-secondary font-semibold mt-2">{s.delivery === 'online' ? 'Online supervision' : s.delivery === 'in_person' ? 'In-person supervision' : 'Online & in-person supervision'}</p>
              {!s.is_demo && (s.verified_on || s.license_expiration) && (
                <p className="text-white/70 text-xs mt-1" data-testid="bhec-verification">
                  {s.verified_on ? `License information verified through Texas BHEC on ${s.verified_on}.` : ''}
                  {s.license_expiration ? ` License expiration: ${s.license_expiration}.` : ''}
                </p>
              )}
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-6">
            {s.accepting_new && !s.is_demo && (
              <p className="text-green-700 font-semibold flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Accepting New Supervisees</p>
            )}
            <ShareListing url={`${window.location.origin}/supervisors/${userId}`} title={`${s.full_name} — Clinical Supervisor`} label="Share Profile" />

            {s.bio && (
              <div>
                <h2 className="font-serif text-lg font-bold text-primary mb-2">About</h2>
                <p className="text-gray-700 leading-relaxed">{s.bio}</p>
              </div>
            )}

            {(s.specialties || []).length > 0 && (
              <div>
                <h2 className="font-serif text-lg font-bold text-primary mb-2">Specialties</h2>
                <div className="flex flex-wrap gap-2">
                  {s.specialties.map((sp) => <Badge key={sp} variant="outline">{sp}</Badge>)}
                </div>
              </div>
            )}

            {(s.populations_served || []).length > 0 && (
              <div>
                <h2 className="font-serif text-lg font-bold text-primary mb-2">Populations Served</h2>
                <div className="flex flex-wrap gap-2">
                  {s.populations_served.map((p) => <Badge key={p} variant="outline">{p}</Badge>)}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              {s.years_experience ? <Info icon={Clock} label="Experience" value={`${s.years_experience}+ years`} /> : null}
              <Info icon={Star} label="Supervision Rate" value={supervisionRate(s)} />
              {s.availability ? <Info icon={Clock} label="Availability" value={s.availability} /> : null}
              {(s.states_covered || []).length ? <Info icon={MapPin} label="States" value={s.states_covered.join(', ')} /> : null}
            </div>

            <div className="border-t pt-6 flex flex-col sm:flex-row gap-3">
              {s.is_demo ? (
                <Button disabled className="bg-gray-100 text-gray-400 cursor-not-allowed flex items-center gap-2" data-testid="sup-not-accepting">
                  <Lock className="w-4 h-4" /> Not currently accepting supervisees
                </Button>
              ) : s.allow_contact === false ? (
                <p className="text-sm text-gray-500">This supervisor prefers to be contacted through their website.</p>
              ) : (
                <>
                  <Button className="bg-primary hover:bg-primary/90 text-white flex items-center gap-2" onClick={() => handleContact('message')} data-testid="contact-supervisor-btn">
                    <Mail className="w-4 h-4" /> Contact Supervisor
                  </Button>
                  <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary/10 flex items-center gap-2" onClick={() => handleContact('consult')} data-testid="request-consultation-btn">
                    <CalendarClock className="w-4 h-4" /> Request Consultation
                  </Button>
                </>
              )}
              {s.website && !s.is_demo && (
                <a href={s.website} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="flex items-center gap-2"><Globe className="w-4 h-4" /> Website</Button>
                </a>
              )}
            </div>
            <p className="text-xs text-gray-400">
              {s.is_demo
                ? 'This supervisor is not currently accepting new supervisees.'
                : 'Contact details are kept private. Your message is delivered securely and the supervisor will reach out to you directly. Listing on this website reflects license information verified through Texas BHEC and is not an endorsement.'}
            </p>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Contact {s.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Your name" value={cName} onChange={(e) => setCName(e.target.value)} data-testid="inquiry-name" />
              <Input placeholder="Your email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} data-testid="inquiry-email" />
              <Input placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="inquiry-phone" />
              <Input placeholder="Your state" value={cState} onChange={(e) => setCState(e.target.value)} data-testid="inquiry-state" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select className="border rounded-md px-2 py-2 text-sm" value={cType} onChange={(e) => setCType(e.target.value)} data-testid="inquiry-type">
                <option value="">Supervision type…</option>
                <option value="Individual">Individual</option>
                <option value="Group">Group</option>
              </select>
              <select className="border rounded-md px-2 py-2 text-sm" value={cFormat} onChange={(e) => setCFormat(e.target.value)} data-testid="inquiry-format">
                <option value="">Format…</option>
                <option value="Online">Online</option>
                <option value="In Person">In Person</option>
              </select>
            </div>
            <Textarea placeholder="Introduce yourself and describe what you're looking for in supervision…"
              value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="inquiry-message" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-primary text-white" onClick={submitInquiry} disabled={sending} data-testid="send-inquiry-btn">
              {sending ? 'Sending…' : 'Send Inquiry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div>
    <p className="text-xs text-gray-400 flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {label}</p>
    <p className="font-medium text-gray-800">{value}</p>
  </div>
);

export default SupervisorProfile;
